import type {
  DefinitionFormSchema,
  DefinitionValidator,
  DomainValidationContext,
  FormErrors,
  ValidationResult,
  ValidationTriggerContext,
  ValidatorConfig,
} from './definition-form-types'
import {
  validateAllowedValuesList,
  validateDefaultValue,
  validateDescription,
  validateNewAllowedValue,
  validatePropertyName,
  validateRegex,
} from './definition-form-validators'

/**
 * Orchestrates asynchronous validation for a definition form by executing a sequence
 * of validators and aggregating their results into a single error state.
 *
 * Validators can contribute both form-level and field-level errors. Results from
 * multiple validators are merged (not overwritten), preserving all messages.
 *
 * @typeParam Schema - The shape of the form data being validated. Must extend DefinitionFormSchema.
 *
 * @remarks
 * - Use validateFormAsync to run all validators from a clean error state.
 * - Use validateFieldAsync to validate a single field while preserving other fields' errors.
 * - Validators are executed sequentially; each validator receives the progressively
 *   accumulated error state.
 */
class DefinitionForm<Schema extends DefinitionFormSchema> {
  #validators: Array<ValidatorConfig<Schema>>

  constructor(validators: Array<ValidatorConfig<Schema>>) {
    this.#validators = validators
  }

  /**
   * Validates the entire form from a clean error state.
   *
   * Initializes with empty formErrors and fieldErrors, runs all validators, and returns
   * the combined FormErrors produced by the sequence.
   *
   * @param data - The current form data to validate.
   * @param context - Domain-specific context supplied to each validator.
   * @returns A promise that resolves to the aggregated form and field errors.
   */
  async validateFormAsync(data: Schema, context: DomainValidationContext): Promise<FormErrors<Schema>> {
    // Form validation always starts with a clean state
    const errors = {formErrors: [], fieldErrors: {}}
    return this.#parseAsync(data, errors, {validationType: 'form'}, context)
  }

  /**
   * Validates a single field while preserving other fields' errors.
   *
   * Resets the specified field’s error list to empty before running field-targeted validators,
   * then merges any new errors back into the provided error state.
   *
   * @param field - The key of the field to validate.
   * @param data - The current form data to validate.
   * @param errors - The existing error state; errors for other fields are preserved.
   * @param context - Domain-specific context supplied to each validator.
   * @returns A promise that resolves to the merged error state after validation.
   */
  async validateFieldAsync(
    field: keyof Schema,
    data: Schema,
    errors: FormErrors<Schema>,
    context: DomainValidationContext,
  ): Promise<FormErrors<Schema>> {
    const initialErrors = {
      formErrors: errors.formErrors || [],
      fieldErrors: {
        ...errors.fieldErrors,
        // Start validating field with an empty state
        [field]: [],
      },
    }
    return this.#parseAsync(data, initialErrors, {validationType: 'field', field}, context)
  }

  /**
   * Executes the appropriate validators (form-level or field-level) and merges their results
   * into the provided error state.
   *
   * When validationType is "form", all validators run. When "field", only validators targeting
   * the specified field run. Each validator receives the progressively merged error state.
   *
   * @param data - The current form data to validate.
   * @param errors - The initial error state to merge results into.
   * @param validationContext - Indicates the trigger: entire form or a specific field.
   * @param domainContext - Domain-specific context passed to each validator.
   * @returns A promise that resolves to the accumulated error state from all executed validators.
   */
  async #parseAsync(
    data: Schema,
    errors: FormErrors<Schema>,
    validationContext: ValidationTriggerContext<Schema>,
    domainContext: DomainValidationContext,
  ): Promise<FormErrors<Schema>> {
    let foundErrors = errors
    const validators =
      validationContext.validationType === 'form'
        ? this.#validators
        : this.#validators.filter(v => v.field === validationContext.field)

    for (const {validate} of validators) {
      const result = await validate(data, foundErrors, domainContext)
      foundErrors = this.#mergeValidationResults(foundErrors, result)
    }

    return foundErrors
  }

  /**
   * Merges a single validator’s ValidationResult into the accumulated FormErrors.
   *
   * @param errors - The current accumulated error state.
   * @param result - The validator result to merge; if omitted, the original error state is returned.
   * @returns The merged error state.
   */
  #mergeValidationResults(errors: FormErrors<Schema>, result: ValidationResult<Schema> = {}): FormErrors<Schema> {
    // Get all unique field keys from both objects
    const allFieldKeys = new Set([...Object.keys(errors.fieldErrors || {}), ...Object.keys(result.fieldErrors || {})])

    const fieldErrors = Object.fromEntries(
      Array.from(allFieldKeys).map(field => {
        const existingErrors = errors.fieldErrors[field as keyof Schema] || []
        const newErrors = result.fieldErrors?.[field as keyof Schema] || []
        return [field, existingErrors.concat(newErrors)]
      }),
    ) as Partial<Record<keyof Schema, string[]>>

    return {
      formErrors: errors.formErrors.concat(result.formErrors || []),
      fieldErrors,
    }
  }
}

export type DefinitionFormType<Schema extends DefinitionFormSchema> = DefinitionForm<Schema>

/**
 * Creates a DefinitionForm pre-configured with the standard validators for a custom property definition.
 *
 * The validator pipeline is assembled in the following order:
 * 1) propertyName
 * 2) description
 * 3) newAllowedValue
 * 4) allowedValues list (form-level; `field: undefined`)
 * 5) regexPattern
 * 6) defaultValue
 * followed by any validators supplied via `additionalValidators`.
 *
 * Use `additionalValidators` to extend the default rules with domain-specific checks. They are appended
 * and executed after the built-ins; if you need to alter or supersede behavior, place your validator later
 * in the sequence and implement the desired logic there.
 *
 * @param additionalValidators - Optional extra validators to append to the default set. Defaults to an empty array.
 * @returns A {@link DefinitionForm} instance parameterized by `Schema`, with a composed validator pipeline.
 *
 * @remarks
 * - A validator with `field: undefined` is treated as a form-level validator and is not run for individual fields.
 * - Validator order is significant; later validators run after earlier ones and may rely on their outcomes.
 */
export function createDefinitionForm<Schema extends DefinitionFormSchema>(
  additionalValidators: Array<ValidatorConfig<Schema>> = [],
): DefinitionForm<Schema> {
  return new DefinitionForm<Schema>([
    {field: 'propertyName', validate: validatePropertyName as DefinitionValidator<Schema>},
    {field: 'description', validate: validateDescription as DefinitionValidator<Schema>},
    {field: 'newAllowedValue', validate: validateNewAllowedValue as DefinitionValidator<Schema>},
    {field: undefined, validate: validateAllowedValuesList as DefinitionValidator<Schema>},
    {field: 'regexPattern', validate: validateRegex as DefinitionValidator<Schema>},
    {field: 'defaultValue', validate: validateDefaultValue as DefinitionValidator<Schema>},
    ...additionalValidators,
  ])
}
